<template>
  <div
    ref="notificationContainer"
    class="notification-item"
    :class="{
      'notification-item--read': notification.is_read,
      [`notification-item--${notification.type.replaceAll('_', '-')}`]: true,
    }"
    v-bind="$attrs"
  >
    <div
      class="notification-item__header"
      @click.prevent.stop="onClick"
    >
      <div
        v-if="shouldShowIcon"
        class="notification-item__icon"
      />
      <span class="notification-item__title">
        {{ notification.title }}
      </span>
      <span class="notification-item__ago">
        {{ ago }}
      </span>
    </div>
    <SafeHtml
      class="notification-item__text"
      :raw-html="displayHtml"
      @click.self="onClick"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'

import { useRoute, useRouter } from 'vue-router'
import type { NotificationPayload } from '@/store/types'
import SafeHtml from '@/uiKit/SafeHtml.vue'
import { replaceableRegex } from '@/core/utils/regex'
import { getAgoString } from '@/core/utils/time'

import {
  extractData,
  formatComment,
  formatNewDatasetVersion,
  formatPaymentFailed,
  formatWorkAssigned,
  workflowUrl,
} from './notification'
import { useNotificationsChannel } from './useNotificationsChannel'

const props = defineProps<{
  notification: NotificationPayload
  isOldest?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const router = useRouter()
const route = useRoute()

const displayHtml = computed<string>(() => {
  // NOTE: Whitespace in generated html is important. Do not remove it by accident!
  switch (props.notification.type) {
    case 'account_limit_reached':
    case 'payment_failed':
      return formatPaymentFailed(props.notification)

    case 'new_dataset_version':
      return formatNewDatasetVersion(props.notification)

    case 'workflow_image_comment':
    case 'comment_created':
    case 'comment_updated':
    case 'comment_with_restricted_mention_created':
    case 'comment_with_restricted_mention_updated':
      return formatComment(props.notification)

    case 'work_assigned':
    case 'work_rejected':
      return formatWorkAssigned(props.notification)
    default:
      return props.notification.parsed
  }
})

const shouldShowIcon = computed<boolean>(() =>
  [
    'account_limit_reached',
    'task_approved',
    'task_assigned',
    'task_ready_for_review',
    'task_rejected',
    'work_assigned',
    'work_rejected',
    'workflow_image_comment',
    'comment_created',
    'comment_updated',
    'comment_with_restricted_mention_created',
    'comment_with_restricted_mention_updated',
  ].includes(props.notification.type),
)

const ago = computed<string>(() => getAgoString(props.notification.inserted_at))

const wasVisible = ref(false)

const channel = useNotificationsChannel()

const markReadIfWasVisible = (): void => {
  if (!wasVisible.value) {
    return
  }
  if (props.notification.is_read) {
    return
  }

  channel.markNotificationRead(props.notification.id)
}

onBeforeUnmount(() => {
  markReadIfWasVisible()
})

const handleBillingNotification = (): void => {
  const alreadyOnRoute = route.query.settings === 'plans'
  if (alreadyOnRoute) {
    return
  }
  router.push({ query: { ...route.query, settings: 'plans' } })
}

const handleExportNotification = (): void => {
  if (!props.notification.dataset_id) {
    return
  }
  emit('close')
  const path = `/datasets/${props.notification.dataset_id}?export`
  const alreadyOnRoute = route.path === path
  if (alreadyOnRoute) {
    return
  }
  router.push(path)
}

const alreadyOnRoute = (newRoute: string): boolean => route.fullPath === newRoute

const handleWorkNotification = (): void => {
  if (!props.notification.dataset_id) {
    return
  }
  const { value: info } = extractData(props.notification, replaceableRegex('Info'))
  const firstSeq = info.split(';')[1].split(',')[0]
  const newRoute = workflowUrl(props.notification.dataset_id, firstSeq)

  if (alreadyOnRoute(newRoute)) {
    return
  }
  router.push(newRoute)
}

const handleWorkflowImageCommentNotification = (): void => {
  if (!props.notification.dataset_id) {
    return
  }
  const { value: seq } = extractData(props.notification, replaceableRegex('DatasetItem'))
  const newRoute = workflowUrl(props.notification.dataset_id, seq)

  if (alreadyOnRoute(newRoute)) {
    return
  }
  router.push(newRoute)
}

const onClick = (): void => {
  switch (props.notification.type) {
    case 'account_limit_reached':
    case 'payment_failed':
      return handleBillingNotification()

    case 'new_dataset_version':
      return handleExportNotification()

    case 'work_assigned':
    case 'work_rejected':
      return handleWorkNotification()

    case 'workflow_image_comment':
    case 'comment_created':
    case 'comment_updated':
    case 'comment_with_restricted_mention_created':
    case 'comment_with_restricted_mention_updated':
      return handleWorkflowImageCommentNotification()
  }
}

const notificationContainer = ref<HTMLElement>()

const isVisible = ref(false)

const { stop } = useIntersectionObserver(notificationContainer, ([{ isIntersecting }]) => {
  isVisible.value = isIntersecting
})

watch(isVisible, (newIsVisible: boolean): void => {
  if (newIsVisible && props.isOldest) {
    channel.loadMoreNotifications()
  }
  wasVisible.value = newIsVisible
})

onBeforeUnmount(() => {
  stop()
})
</script>

<style lang="scss" scoped>
.notification-item {
  background: var(--color-background-gray-sunken);
  border-radius: 8px;
  padding: 12px;

  &:hover {
    background: var(--color-background-transparent-hovered);
    cursor: pointer;
    opacity: 1;
  }

  &--read {
    background: var(--color-surface-default);
    opacity: 0.5;
  }

  &__header {
    display: flex;
    column-gap: 8px;
    margin-bottom: 8px;
    align-items: center;
  }

  &__icon {
    height: 16px;
    width: 16px;
    display: flex;
  }

  &__title {
    flex: 1 1 auto;
    @include typographyBoldBody200;
    color: var(--color-text-default);
  }

  &__ago {
    justify-content: flex-end;
    text-align: right;
    @include typographyBoldBody100;
    color: var(--color-text-subtlest);
  }

  &__text {
    @include typographyRegularBody100;
    color: var(--color-text-subtle);
  }

  &--task-assigned,
  &--work-assigned {
    .notification-item__title {
      color: var(--color-background-stages-annotate-default);
    }

    .notification-item__icon {
      background: var(--color-background-stages-annotate-default);
      mask-image: url(NotificationItem_work_assigned.svg);
    }
  }

  &--task-approved {
    .notification-item__title {
      color: var(--color-background-stages-complete-default);
    }

    .notification-item__icon {
      background: var(--color-background-stages-complete-default);
      mask-image: url(NotificationItem_task_approved.svg);
    }
  }

  &--task-ready-for-review {
    .notification-item__title {
      color: var(--color-background-stages-review-default);
    }

    .notification-item__icon {
      background: var(--color-text-rainbow-04);
      mask-image: url(NotificationItem_task_ready_for_review.svg);
    }
  }

  &--account-limit-reached,
  &--task-rejected,
  &--work-rejected {
    .notification-item__title {
      color: var(--color-text-critical);
    }

    .notification-item__icon {
      background: var(--color-icon-critical);
    }
  }

  &--account-limit-reached {
    .notification-item__icon {
      mask-image: url(NotificationItem_account_limit_reached.svg);
    }
  }

  &--task-rejected,
  &--work-rejected {
    .notification-item__icon {
      mask-image: url(NotificationItem_work_rejected.svg);
    }
  }

  &--workflow-image-comment,
  &--comment-created,
  &--comment-updated,
  &--comment-with-restricted-mention-created,
  &--comment-with-restricted-mention-updated {
    .notification-item__icon {
      background: var(--color-icon-gray-subtle);
      mask-image: url(NotificationItem_workflow_image_comment.svg);
    }
  }
}
</style>

<!--
  the __bold and __text-link classes are added as raw html, not
  in the template, so we can't use them in scoped mode
-->
<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type-->
<style lang="scss">
.notification-item {
  &__bold,
  &__text-link {
    @include typographyBoldBody100;
    color: var(--color-text-default);
  }

  &__text-link {
    &:hover {
      color: var(--color-link-default);
    }
  }
}
</style>
